import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { SortDirection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { Trans, useTranslation } from '@frontend/i18n';
import { BulkEmailPrefixes, BulkTextPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { ContentLoader, Text, TextLink } from '@frontend/design-system';
import { useBulkNavigator } from '../../../hooks/use-bulk-navigator';
import { BulkMessageError } from '../../bulk-message-error';
import { useHasAllotment } from '../../hooks/use-has-allotment';
import { CampaignGroupTable } from '../campaign-group-table/campaign-group-table';
import { useCampaignGroups } from './use-campaign-groups';
import { Welcome } from './welcome';

type Props = {
  campaignType: CampaignType;
  selectedLocationIds: string[];
  selectedSort: SortDirection;
  selectedYear: number;
};

export const CampaignListBody = ({ campaignType, selectedLocationIds, selectedSort, selectedYear }: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const navigateTo = useBulkNavigator(campaignType);

  const {
    hasAllotment,
    isError: isHasAllotmentError,
    isLoading: isHasAllotmentLoading,
  } = useHasAllotment({
    campaignType,
    selectedLocationIds,
  });

  const {
    campaignGroups,
    isError: isCampaignGroupsError,
    isLoading: isCampaignGroupsLoading,
  } = useCampaignGroups({
    campaignType,
    selectedLocationIds,
    selectedSort,
    selectedYear,
  });

  const isLoading = isCampaignGroupsLoading || isHasAllotmentLoading;
  const isError = isCampaignGroupsError || isHasAllotmentError;

  const hasSelectedLocations = selectedLocationIds.length > 0;
  const hasCampaigns = campaignGroups.length > 0;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (isLoading) {
    return (
      <div css={{ position: 'relative', minHeight: '400px' }}>
        <ContentLoader
          show
          message={
            campaignType === CampaignType.EMAIL ? t('Loading Email Campaigns...') : t('Loading Text Campaigns...')
          }
        />
      </div>
    );
  }

  // TODO: Replace with better error state experience
  if (isError) {
    return (
      <BulkMessageError
        heading={
          campaignType === CampaignType.EMAIL
            ? t('An unexpected error occurred while loading your email campaigns.')
            : t('An unexpected error occurred while loading your text campaigns.')
        }
      />
    );
  }

  // TODO: Replace with a better "nothing selected" state experience
  if (!hasSelectedLocations) {
    return (
      <Text size='large' css={{ marginTop: theme.spacing(15), textAlign: 'center' }}>
        {campaignType === CampaignType.EMAIL
          ? t('Select at least one location to view your email campaigns.')
          : t('Select at least one location to view your text campaigns.')}
      </Text>
    );
  }

  if (!hasAllotment && !hasCampaigns) {
    return <Welcome campaignType={campaignType} />;
  }

  // TODO: Replace with better empty state experience
  if (!hasCampaigns) {
    return (
      <Text size='large' css={{ marginTop: theme.spacing(15), textAlign: 'center' }}>
        {campaignType === CampaignType.EMAIL ? (
          <Trans t={t}>
            You have not sent any email campaigns yet.
            <br />
            Click <TextLink onClick={() => navigateTo.editor()}>here</TextLink> to get started.
          </Trans>
        ) : (
          <Trans t={t}>
            You have not sent any text campaigns yet.
            <br />
            Click <TextLink onClick={() => navigateTo.editor()}>here</TextLink> to get started.
          </Trans>
        )}
      </Text>
    );
  }

  return (
    <>
      {campaignGroups.map((campaignGroup, index) => {
        const isCurrentMonth = selectedYear === currentYear && campaignGroup.month === currentMonth;
        return (
          <CampaignGroupTable
            campaignGroup={campaignGroup}
            campaignType={campaignType}
            graphSize={isCurrentMonth ? 'large' : 'small'}
            hasAllotment={hasAllotment}
            key={index}
            selectedLocationIds={selectedLocationIds}
            trackingIdPrefix={
              campaignType === CampaignType.EMAIL ? BulkEmailPrefixes.Campaigns : BulkTextPrefixes.Campaigns
            }
            year={selectedYear}
          />
        );
      })}
    </>
  );
};
